import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { type FC, useState } from 'react';

import SummaryStat from './SummaryStat.js';
import {
  type ActivityAccuracyGroupBy,
  type ActivityAccuracyTotals,
  type ActivityAccuracyWorstEntry,
  type ActivityUsageGranularity,
  useActivityAccuracy,
} from '../../api/index.js';

const GROUP_BY_OPTIONS: Array<{ value: ActivityAccuracyGroupBy; label: string }> = [
  { value: 'code', label: 'Code' },
  { value: 'patient', label: 'Patient' },
  { value: 'camera', label: 'Camera' },
  { value: 'model', label: 'Model' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'promptVersion', label: 'Prompt' },
  { value: 'lighting', label: 'Lighting' },
  { value: 'dimension', label: 'Dimension' },
];

function formatAgreement(agreement: number | null): string {
  return agreement === null ? '—' : `${Math.round(agreement * 100)}%`;
}

function formatWrongPerson(totals: ActivityAccuracyTotals | undefined): string {
  if (!totals || totals.wrongPerson.rate === null) {
    return '—';
  }
  return `${formatAgreement(totals.wrongPerson.rate)} (${totals.wrongPerson.events}/${totals.reviewedWithDimensions})`;
}

interface WorstCardProps {
  title: string;
  entry: ActivityAccuracyWorstEntry | null;
  minReviewed: number;
}

const WorstCard: FC<WorstCardProps> = ({ title, entry, minReviewed }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
      {title}
    </Typography>
    {entry ? (
      <>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {entry.label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatAgreement(entry.agreement)} agreement · {entry.reviewed.toLocaleString()} reviewed
        </Typography>
      </>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Nothing with at least {minReviewed} reviews in this period.
      </Typography>
    )}
  </Paper>
);

interface CorrectionsTableProps {
  columns: string[];
  rows: Array<{ key: string; cells: string[]; count: number }>;
  emptyText: string;
  loading: boolean;
}

const CorrectionsTable: FC<CorrectionsTableProps> = ({ columns, rows, emptyText, loading }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell key={column}>{column}</TableCell>
          ))}
          <TableCell align="right">Count</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key} hover>
            {row.cells.map((cell, index) => (
              <TableCell key={columns[index]}>{cell}</TableCell>
            ))}
            <TableCell align="right">{row.count.toLocaleString()}</TableCell>
          </TableRow>
        ))}
        {!loading && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length + 1} align="center" sx={{ color: 'text.secondary', py: 3 }}>
              {emptyText}
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  </TableContainer>
);

interface ActivityAccuracyPanelProps {
  from: string;
  to: string;
  granularity: ActivityUsageGranularity;
}

const ActivityAccuracyPanel: FC<ActivityAccuracyPanelProps> = ({ from, to, granularity }) => {
  const [groupBy, setGroupBy] = useState<ActivityAccuracyGroupBy>('code');

  const accuracy = useActivityAccuracy(
    { from, to, granularity, groupBy },
    { enabled: Boolean(from && to), keepPreviousData: true },
  );

  const totals = accuracy.data?.totals;
  const worst = accuracy.data?.worst;
  const items = accuracy.data?.items ?? [];
  const confusion = accuracy.data?.confusion ?? [];
  const dimensionConfusion = accuracy.data?.dimensionConfusion ?? [];

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <SummaryStat label="Agreement" value={formatAgreement(totals?.agreement ?? null)} emphasised />
            <SummaryStat label="Reviewed" value={(totals?.reviewed ?? 0).toLocaleString()} />
            <SummaryStat label="Accepted" value={(totals?.accepted ?? 0).toLocaleString()} />
            <SummaryStat label="Rejected" value={(totals?.rejected ?? 0).toLocaleString()} />
            <SummaryStat label="Unreviewed" value={(totals?.unreviewed ?? 0).toLocaleString()} />
            <SummaryStat label="Wrong person" value={formatWrongPerson(totals)} />
          </Stack>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={groupBy}
            onChange={(_, value: ActivityAccuracyGroupBy | null) => value && setGroupBy(value)}
          >
            {GROUP_BY_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {worst ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <WorstCard title="Worst dimension" entry={worst.dimension} minReviewed={worst.minReviewed} />
          <WorstCard title="Worst camera" entry={worst.camera} minReviewed={worst.minReviewed} />
        </Stack>
      ) : null}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{granularity === 'month' ? 'Month' : 'Day'}</TableCell>
              <TableCell>{GROUP_BY_OPTIONS.find((option) => option.value === groupBy)?.label}</TableCell>
              <TableCell align="right">Reviewed</TableCell>
              <TableCell align="right">Accepted</TableCell>
              <TableCell align="right">Rejected</TableCell>
              <TableCell align="right">Agreement</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={`${item.period}::${item.key}`} hover>
                <TableCell>{item.period}</TableCell>
                <TableCell>{item.label}</TableCell>
                <TableCell align="right">{item.reviewed.toLocaleString()}</TableCell>
                <TableCell align="right">{item.accepted.toLocaleString()}</TableCell>
                <TableCell align="right">{item.rejected.toLocaleString()}</TableCell>
                <TableCell align="right">{formatAgreement(item.agreement)}</TableCell>
              </TableRow>
            ))}
            {!accuracy.isLoading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                  {groupBy === 'dimension'
                    ? 'No reviews with dimensions in this period.'
                    : 'No reviewed AI events in this period.'}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1">Corrections</Typography>
      <CorrectionsTable
        columns={['AI code', 'Corrected to']}
        rows={confusion.map((entry) => ({
          key: `${entry.aiCode}::${entry.reviewedCode}`,
          cells: [entry.aiCode, entry.reviewedCode],
          count: entry.count,
        }))}
        emptyText="No corrections in this period."
        loading={accuracy.isLoading}
      />

      <Typography variant="subtitle1">Dimension corrections</Typography>
      <CorrectionsTable
        columns={['Dimension', 'AI value', 'Reviewed value']}
        rows={dimensionConfusion.map((entry) => ({
          key: `${entry.dimension}::${entry.aiValue}::${entry.reviewedValue}`,
          cells: [entry.dimension, entry.aiValue, entry.reviewedValue],
          count: entry.count,
        }))}
        emptyText="No dimension corrections in this period."
        loading={accuracy.isLoading}
      />
    </Stack>
  );
};

export default ActivityAccuracyPanel;
